import React, { useState } from 'react';
import { Button } from '../ui/button';
import SignUpPopup from '../SignUpPopup';
import ContactPopup from '../ContactPopup';
import { ExternalLink } from 'lucide-react';

const Footer = () => {
  const [showSignUp, setShowSignUp] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const currentYear = new Date().getFullYear();

  return (
    <>
      <footer className="bg-white border-t border-gray-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-10">
            {/* Logo & App Download */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1">
              <div className="flex items-center gap-1 mb-4">
                <img 
                  src="/buildpilot-logo-black.webp" 
                  alt="BuildPilot App - Australia's Home Building Companion" 
                  className="w-36 h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-gray-600 text-sm mb-6">
                Self-guided tools, education, and guidance for your building journey.
              </p>

              <p className="font-semibold text-gray-900 text-sm mb-3">Get the BuildPilot App</p>
              <div className="flex flex-row gap-3 items-center h-[40px]">
                <a href="https://apps.apple.com/au/app/buildpilot/id6477372083" target="_blank" rel="noopener noreferrer" className="flex items-center h-[40px]">
                  <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83" alt="Download BuildPilot App on the App Store for iOS" className="h-[40px] w-auto" loading="lazy" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.buildpilot.android&hl=en_US" target="_blank" rel="noopener noreferrer" className="flex items-center h-[40px]">
                  <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get BuildPilot App on Google Play for Android" className="h-[58px] w-auto" style={{ marginTop: '-9px', marginBottom: '-9px', marginLeft: '-10px' }} loading="lazy" />
                </a>
              </div>

              {/* Social Icons */}
              <div className="flex gap-4 mt-8">
                <a href="https://www.instagram.com/buildpilot_app?igsh=MW10Z2Zmb2U5ZndyMQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-600 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://www.tiktok.com/@buildpilot_app?_r=1&_t=ZS-93IplmGuPLM" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-600 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              </div>
            </div>

            {/* Project Types */}
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Projects</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li><a href="/swimming-pools" className="hover:text-purple-600 transition-colors">Swimming Pools</a></li>
                <li><a href="/decking" className="hover:text-purple-600 transition-colors">Decking</a></li>
                <li><a href="/pergolas-verandahs" className="hover:text-purple-600 transition-colors">Pergolas & Verandahs</a></li>
                <li><a href="/landscaping" className="hover:text-purple-600 transition-colors">Landscaping</a></li>
                <li><a href="/sheds-garages" className="hover:text-purple-600 transition-colors">Sheds & Garages</a></li>
              </ul>
            </div>

            {/* Tools */}
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Tools</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li><a href="/tools/cost-tracker" className="hover:text-purple-600 transition-colors">Cost Tracker</a></li>
                <li><a href="/tools/licence-check" className="hover:text-purple-600 transition-colors">Builder Licence Check</a></li>
                <li><a href="/tools/quote-comparison" className="hover:text-purple-600 transition-colors">Quote Comparison</a></li>
                <li><a href="/tools/defects-scanner" className="hover:text-purple-600 transition-colors">Defects Scanner</a></li>
                <li><a href="/tools" className="hover:text-purple-600 transition-colors font-medium">View All Tools →</a></li>
              </ul>
            </div>

            {/* BuildPilot App */}
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">BuildPilot</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li><a href="/blog" className="hover:text-purple-600 transition-colors">Blog</a></li>
                <li><a href="/about" className="hover:text-purple-600 transition-colors">About</a></li>
                <li><a href="/contact" className="hover:text-purple-600 transition-colors">Contact</a></li>
                <li><a href="/community" className="hover:text-purple-600 transition-colors">Community</a></li>
                <li><a href="/advertise" className="hover:text-purple-600 transition-colors">Advertise with us</a></li>
                <li>
                  <button 
                    onClick={() => setShowSignUp(true)} 
                    className="hover:text-purple-600 transition-colors text-left"
                  >
                    Sign Up for Updates
                  </button>
                </li>
              </ul>
            </div>

            {/* Planning Guides */}
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Planning Guides</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li><a href="/7-star-energy-rating" className="hover:text-purple-600 transition-colors">7-Star Energy Rating</a></li>
                <li><a href="/all-electric-home" className="hover:text-purple-600 transition-colors">All-Electric Home</a></li>
                <li><a href="/knockdown-rebuild-costs" className="hover:text-purple-600 transition-colors">Knockdown Rebuild Costs</a></li>
                <li><a href="/fixed-price-vs-cost-plus" className="hover:text-purple-600 transition-colors">Fixed vs Cost-Plus</a></li>
                <li><a href="/guides" className="hover:text-purple-600 transition-colors font-medium">All Guides →</a></li>
              </ul>
            </div>

            {/* Support & Legal */}
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Support & Legal</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>
                  <a 
                    href="https://www.buildpilot.com.au" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-purple-600 transition-colors inline-flex items-center gap-1"
                  >
                    BuildPilot Advisory Service
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li><a href="/terms" className="hover:text-purple-600 transition-colors">Terms of Use</a></li>
                <li><a href="/privacy-policy" className="hover:text-purple-600 transition-colors">Privacy Policy</a></li>
                <li><a href="/cookie-policy" className="hover:text-purple-600 transition-colors">Cookie Policy</a></li>
                <li><a href="/security" className="hover:text-purple-600 transition-colors">Security</a></li>
              </ul>
            </div>
          </div>

          {/* Brand Relationship Statement */}
          <div className="border-t border-gray-200 mt-12 pt-8">
            <p className="text-sm text-gray-600 text-center mb-6">
              BuildPilot App is a product by <span className="font-medium text-gray-900">BuildPilot</span>. 
              BuildPilot also offers a{' '}
              <a 
                href="https://www.buildpilot.com.au" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-700 underline underline-offset-2"
              >
                one-on-one home building advisory service
              </a>
              {' '}in some states.
            </p>
          </div>

          {/* Bottom */}
          <div className="border-t border-gray-100 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <a href="/privacy-policy" className="hover:text-purple-600">Privacy Policy</a>
              <a href="/terms" className="hover:text-purple-600">Terms of Use</a>
              <a href="/security" className="hover:text-purple-600">Security</a>
              <a href="/about" className="hover:text-purple-600">About</a>
              <a href="/contact" className="hover:text-purple-600">Contact</a>
            </div>
            <div className="text-xs text-gray-500 text-center md:text-right">
              <p>© {currentYear} BuildPilot. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Popups */}
      <SignUpPopup isOpen={showSignUp} onClose={() => setShowSignUp(false)} />
      <ContactPopup isOpen={showContact} onClose={() => setShowContact(false)} />
    </>
  );
};

export default Footer;
